'use client';

import { useCallback } from 'react';
import { ApiClientError, type LimitErrorCode } from '@/shared/lib/api';
import { useLimitPaywallStore } from '@/shared/stores/limit-paywall-store';
import { getActiveSubscriptionSnapshot } from '@/features/subscription/model/active-subscription.query';
import { queryClient } from '@/shared/lib/query-client';
import { cacheKeys } from '@/shared/lib/cache-invalidation';

/**
 * Optional side-effect callbacks fired when a typed limit error is routed to
 * the server-driven paywall. The paywall UI itself is shown automatically by
 * the root-mounted `<LimitPaywallDialog />` — these callbacks are only for
 * call-site-local cleanup (reset upload UI, warn on preflight drift, etc.).
 */
export interface LimitPaywallHandlers {
  onAngleLibraryLimit?: () => void;
  onConceptAngleLimit?: () => void;
  onProjectLimit?: () => void;
  onProjectReferenceLimit?: () => void;
  onProjectReferenceInsufficientTokens?: () => void;
  onInsufficientTokens?: () => void;
  onRenderQueueLimit?: () => void;
  onRenderDailyLimit?: () => void;
  onRenderMonthlyLimit?: () => void;
  onOutputStyleFeature?: () => void;
  onHighResolutionRenderFeature?: () => void;
  onExtendedThinkingFeature?: () => void;
  onModelSelectionFeature?: () => void;
  onCostEstimateFeature?: () => void;
  onSubscriptionRequired?: () => void;
}

const CODE_TO_HANDLER_KEY: Record<LimitErrorCode, keyof LimitPaywallHandlers> = {
  ANGLE_LIBRARY_LIMIT_EXCEEDED: 'onAngleLibraryLimit',
  CONCEPT_ANGLE_LIMIT_EXCEEDED: 'onConceptAngleLimit',
  PROJECT_LIMIT_EXCEEDED: 'onProjectLimit',
  PROJECT_REFERENCE_LIMIT_EXCEEDED: 'onProjectReferenceLimit',
  PROJECT_REFERENCE_INSUFFICIENT_TOKENS: 'onProjectReferenceInsufficientTokens',
  INSUFFICIENT_TOKENS: 'onInsufficientTokens',
  RENDER_QUEUE_LIMIT_EXCEEDED: 'onRenderQueueLimit',
  RENDER_DAILY_LIMIT_EXCEEDED: 'onRenderDailyLimit',
  RENDER_MONTHLY_LIMIT_EXCEEDED: 'onRenderMonthlyLimit',
  OUTPUT_STYLE_FEATURE_UNAVAILABLE: 'onOutputStyleFeature',
  HIGH_RESOLUTION_RENDER_FEATURE_UNAVAILABLE: 'onHighResolutionRenderFeature',
  EXTENDED_THINKING_FEATURE_UNAVAILABLE: 'onExtendedThinkingFeature',
  // ATLAS model pick without the Pro `model_selection` entitlement — typed
  // 422 with an inline Pass paywall, mirroring EXTENDED_THINKING above.
  MODEL_SELECTION_FEATURE_UNAVAILABLE: 'onModelSelectionFeature',
  COST_ESTIMATE_FEATURE_UNAVAILABLE: 'onCostEstimateFeature',
  SUBSCRIPTION_REQUIRED: 'onSubscriptionRequired',
};

/**
 * Non-hook router. Same logic as [useLimitErrorPaywall] but works outside the
 * React tree (e.g. from `MutationCache.onError`). Returns the matched code
 * when the error was routed to the paywall, or null otherwise so callers can
 * fire any code-specific side effects without re-doing the discriminator.
 */
export function routeLimitErrorToPaywall(error: unknown): LimitErrorCode | null {
  if (!(error instanceof ApiClientError)) return null;

  const code = error.code as LimitErrorCode | undefined;
  if (!code || !(code in CODE_TO_HANDLER_KEY)) return null;

  // 422 limits carry the paywall inline on the error body. SUBSCRIPTION_REQUIRED
  // is a 403 without an inline payload — we fall back to the preflight-cached
  // copy on the effective plan's paywall_payloads map. Other codes like
  // PROJECT_REFERENCE_LIMIT_EXCEEDED don't exist in that map because they
  // split by category (:image / :document) — those always come from the body.
  let payload = error.getPaywall();
  if (!payload && (code === 'SUBSCRIPTION_REQUIRED' || code === 'INSUFFICIENT_TOKENS')) {
    // `paywall_payloads` lives on the plan resource (real plan or guest plan),
    // mirroring `usePaywallPayloads` — `effectivePlan` only holds limits.
    // INSUFFICIENT_TOKENS falls back here only if an older server didn't yet
    // attach the paywall inline; new 402 responses carry it on the error body.
    const plan = getActiveSubscriptionSnapshot().plan;
    const attrs = plan && 'attributes' in plan ? plan.attributes : plan;
    payload = attrs?.paywall_payloads?.[code];
  }
  if (!payload) return null;

  // The error itself signals cache drift — server enforced a limit the
  // local snapshot didn't predict (e.g. admin grant just landed, balance
  // changed on another tab). Refetch the authoritative state so the next
  // click doesn't read the same stale cache and re-hit this path.
  queryClient.invalidateQueries({ queryKey: cacheKeys.balance });
  queryClient.invalidateQueries({ queryKey: cacheKeys.profile });
  queryClient.invalidateQueries({ queryKey: cacheKeys.subscriptions.active });

  useLimitPaywallStore.getState().showPaywall(payload, code);
  return code;
}

/**
 * Central router for typed limit errors. When an error has a server-driven
 * paywall payload, the shared dialog is shown and any matching side-effect
 * callback is invoked. Returns true when handled (caller can skip its
 * generic error branch).
 */
export function useLimitErrorPaywall(
  handlers: LimitPaywallHandlers = {}
): (error: unknown) => boolean {
  return useCallback(
    (error: unknown): boolean => {
      const code = routeLimitErrorToPaywall(error);
      if (!code) return false;
      handlers[CODE_TO_HANDLER_KEY[code]]?.();
      return true;
    },
    [handlers]
  );
}
